import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Sparkles, X, Copy, ExternalLink, CheckCircle2 } from "lucide-react";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import { toast } from "sonner";
import whatsappQrCode from "@/assets/whatsapp-qrcode.png";

const STORAGE_KEY = "rpgcoins_whatsapp_group_dismissed";
const DIALOG_STATE_EVENT = "rpgcoins:whatsapp-group-dialog";

const WhatsAppGroupWelcomeDialog = () => {
  const { data: config } = useConfiguracoes();
  const linkGrupoWhatsapp = config?.link_grupo_whatsapp;
  const qrcodeGrupoUrl = config?.qrcode_grupo_url;

  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showFarewellMsg, setShowFarewellMsg] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (!linkGrupoWhatsapp || shownRef.current) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    const sessionDismissed = sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed && !sessionDismissed) {
      shownRef.current = true;
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [linkGrupoWhatsapp]);

  useEffect(() => {
    if (open) {
      document.body.dataset.whatsappGroupDialogOpen = "true";
    } else {
      delete document.body.dataset.whatsappGroupDialogOpen;
    }

    window.dispatchEvent(new CustomEvent(DIALOG_STATE_EVENT, { detail: { open } }));
  }, [open]);

  useEffect(() => {
    return () => {
      delete document.body.dataset.whatsappGroupDialogOpen;
      window.dispatchEvent(new CustomEvent(DIALOG_STATE_EVENT, { detail: { open: false } }));
    };
  }, []);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, "true");
    }
    sessionStorage.setItem(STORAGE_KEY, "true");
    setOpen(false);
    setShowFarewellMsg(false);
    setDontShowAgain(false);
  };

  const handleToggleDontShow = () => {
    if (!dontShowAgain) {
      setDontShowAgain(true);
      setShowFarewellMsg(true);
    } else {
      setDontShowAgain(false);
      setShowFarewellMsg(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(linkGrupoWhatsapp || "");
    setLinkCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (!linkGrupoWhatsapp) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 z-0 bg-background/45 backdrop-blur-[1px] pointer-events-auto" onClick={handleClose} />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="relative z-10 w-full max-w-sm pointer-events-auto"
          >
            {/* Outer medieval border */}
            <div className="relative tibia-border bg-card rounded-sm overflow-hidden">
              {/* Decorative top banner */}
              <div className="relative bg-gradient-to-b from-primary/30 via-primary/10 to-transparent px-4 pt-5 pb-3">
                {/* Sparkle decorations */}
                <Sparkles className="absolute top-2 left-3 w-3 h-3 text-primary/50 animate-pulse" />
                <Sparkles className="absolute top-4 right-5 w-2.5 h-2.5 text-primary/40 animate-pulse" style={{ animationDelay: "0.5s" }} />
                <Sparkles className="absolute bottom-1 left-8 w-2 h-2 text-primary/30 animate-pulse" style={{ animationDelay: "1s" }} />

                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-secondary hover:bg-destructive/80 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <X className="w-3.5 h-3.5 text-foreground" />
                </button>

                {/* Icon & Title */}
                <div className="flex flex-col items-center gap-2">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full bg-green-600/20 border-2 border-green-500/40 flex items-center justify-center">
                      <MessageCircle className="w-7 h-7 text-green-400" />
                    </div>
                    <motion.div
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-card"
                    />
                  </div>
                  <h2 className="font-pixel text-xs text-primary gold-text-glow tracking-wider text-center uppercase">
                    ⚔️ Comunidade RPG Coins
                  </h2>
                  <p className="text-[10px] text-muted-foreground text-center leading-relaxed max-w-[260px]">
                    Entre no nosso grupo exclusivo no WhatsApp! Fique por dentro das melhores ofertas e novidades.
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="px-4 pb-4 pt-2 flex flex-col items-center gap-3">
                {/* QR Code */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="tibia-border-inner bg-white rounded-sm p-2"
                >
                  <img
                    src={whatsappQrCode}
                    alt="QR Code Grupo WhatsApp"
                    className="w-44 h-44 object-contain"
                  />
                </motion.div>

                <p className="font-pixel text-[7px] text-muted-foreground text-center">
                  Escaneie o QR Code ou clique no botão abaixo
                </p>

                {/* Action Buttons */}
                <div className="flex gap-2 w-full">
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    href={linkGrupoWhatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white font-pixel text-[8px] py-2.5 rounded-sm transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Entrar no Grupo
                  </motion.a>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleCopyLink}
                    className="flex items-center justify-center gap-1.5 tibia-border bg-secondary/50 hover:bg-secondary font-pixel text-[8px] text-muted-foreground px-3 py-2.5 rounded-sm transition-colors"
                  >
                    {linkCopied ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                    {linkCopied ? "Copiado!" : "Copiar"}
                  </motion.button>
                </div>


                {/* Divider */}
                <div className="w-full border-t border-border/40 my-1" />

                {/* Don't show again */}
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div
                    onClick={handleToggleDontShow}
                    className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-all ${
                      dontShowAgain
                        ? "bg-primary border-primary"
                        : "border-muted-foreground/40 group-hover:border-primary/60"
                    }`}
                  >
                    {dontShowAgain && <CheckCircle2 className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <span
                    onClick={handleToggleDontShow}
                    className="font-pixel text-[7px] text-muted-foreground group-hover:text-foreground transition-colors select-none"
                  >
                    Não exibir novamente
                  </span>
                </label>

                {/* Farewell message */}
                <AnimatePresence>
                  {showFarewellMsg && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="w-full overflow-hidden"
                    >
                      <div className="tibia-border-inner bg-primary/10 rounded-sm p-2.5 text-center">
                        <p className="font-pixel text-[7px] text-primary leading-relaxed">
                          💡 Sem problemas! Você pode acessar o grupo a qualquer momento clicando em{" "}
                          <span className="underline font-bold">"Grupo WhatsApp"</span> no rodapé do site.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default WhatsAppGroupWelcomeDialog;
